// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Droplets, Eye, EyeOff, Users } from 'lucide-react';
import api from '../utils/api';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await api.get(`/auth/invite/${token}`);
        setInviteInfo(res.data);
        if (res.data.status !== 'pending') {
          setError('This invitation has already been used or expired.');
        }
      } catch (err) {
        setError('Invalid or expired invitation link.');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setAccepting(true);
    setError('');
    try {
      const payload = { token };
      if (!inviteInfo?.existing_user) {
        payload.name = form.name;
        payload.password = form.password;
      }
      const res = await api.post('/auth/accept-invite', payload);
      login(res.data.user, res.data.token, res.data.org, res.data.role);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0891B2]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-[#0891B2] flex items-center justify-center">
            <Droplets size={20} className="text-white" />
          </div>
          <span className="text-xl font-bold text-gray-900 tracking-tight">Pourix</span>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          {error ? (
            <div className="text-center py-4">
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <Link to="/login" className="text-sm text-[#0891B2] font-medium hover:underline">Go to login</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center">
                  <Users size={20} className="text-[#0891B2]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">You're invited!</h2>
                  <p className="text-sm text-gray-500">
                    Join <span className="font-semibold text-gray-700">{inviteInfo?.org_name}</span> as {inviteInfo?.role}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-6">
                <p className="text-xs text-gray-500">Invitation for</p>
                <p className="text-sm font-medium text-gray-900">{inviteInfo?.email}</p>
              </div>

              <form onSubmit={handleAccept} className="space-y-4">
                {!inviteInfo?.existing_user && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none"
                        placeholder="John Smith" required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'} value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none pr-10"
                          placeholder="Min 6 characters" required minLength={6}
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <button
                  type="submit" disabled={accepting}
                  className="w-full py-2.5 bg-[#0891B2] text-white font-semibold rounded-lg hover:bg-[#0e7490] transition-colors disabled:opacity-60 text-sm"
                >
                  {accepting ? 'Joining...' : `Join ${inviteInfo?.org_name}`}
                </button>
              </form>

              {inviteInfo?.existing_user && (
                <p className="text-center text-xs text-gray-500 mt-4">
                  You already have a Pourix account. Click above to join this organization.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
